const { abs, ceil, max } = Math
import { generateDateTicks, Plot, usePlotlyHoverDismiss, useTheme as usePlotTheme } from 'pltly'
import { useState, useMemo, useCallback, useEffect, useRef, memo } from 'react'
import { useAction, useActions } from 'use-kbd'
import { useUrlState } from 'use-prms'
import { ChartControls, metricConfig, getRangeFloor } from './ChartControls'
import { CustomLegend } from './CustomLegend'
import { DataTable } from './DataTable'
import { ExamplesBar } from './ExamplesBar'
import { TIME_WINDOWS, getWindowForDuration } from '../hooks/useDataAggregation'
import { useLatestMode } from '../hooks/useLatestMode'
import { useMetrics } from '../hooks/useMetrics'
import { useMobile } from '../hooks/useMobile'
import { useMultiDeviceAggregation } from '../hooks/useMultiDeviceAggregation'
import { useTimeRangeParam } from '../hooks/useTimeRangeParam'
import { deviceRenderStrategyParam, hsvConfigParam, intFromList, rangeFloorsParam, rawOpacityParam, SMOOTHING_AUTO, smoothingParam, stddevOpacityParam, xGroupingParam } from '../lib/urlParams'
import { formatForPlotly } from '../utils/dateFormat'
import { getDeviceLineProps } from '../utils/deviceRenderStrategy'
import type { PxOption } from './AggregationControl'
import type { DeviceDataResult } from './DevicePoller'
import type { Metric } from '../lib/urlParams'
import type { Device } from '../services/awairService'
import type { DataSummary } from '../types/awair'
import type { Data, PlotRelayoutEvent } from 'plotly.js'

// Extend Data type to include zorder (supported by plotly.js but not in @types/plotly.js); https://github.com/DefinitelyTyped/DefinitelyTyped/pull/74155 will fix
type DataWithZorder = Data & { zorder?: number }

const noop = () => {}

/** Split band data into contiguous segments at null gaps (so Plotly doesn't interpolate across gaps) */
function splitBandSegments(timestamps: string[], upper: (number | null)[], lower: (number | null)[]) {
  const segments: Array<{ timestamps: string[]; upper: (number | null)[]; lower: (number | null)[] }> = []
  let cur: typeof segments[number] | null = null
  for (let i = 0; i < timestamps.length; i++) {
    if (upper[i] === null || lower[i] === null) {
      if (cur && cur.timestamps.length > 0) segments.push(cur)
      cur = null
    } else {
      if (!cur) cur = { timestamps: [], upper: [], lower: [] }
      cur.timestamps.push(timestamps[i])
      cur.upper.push(upper[i])
      cur.lower.push(lower[i])
    }
  }
  if (cur && cur.timestamps.length > 0) segments.push(cur)
  return segments
}

export type HasDeviceIdx = { deviceIdx: number }
export type HasMetric = { metric: 'primary' | 'secondary' }

export type LegendHoverState =
  | { type: 'device' } & HasDeviceIdx
  | { type: 'trace' } & HasDeviceIdx & HasMetric
  | { type: 'metric' } & HasMetric
  | null

interface Props {
  deviceDataResults: DeviceDataResult[]
  summary: DataSummary | null
  devices: Device[]
  selectedDeviceIds: number[]
  onDeviceSelectionChange: (deviceIds: number[]) => void
  onHoverDeviceId?: (deviceId: number | null) => void
  timeRange: { timestamp: Date | null; duration: number }
  setTimeRange: (range: { timestamp: Date | null; duration: number }) => void
  isOgMode?: boolean
}

export const AwairChart = memo(function AwairChart(
  {
    deviceDataResults,
    summary,
    devices,
    selectedDeviceIds,
    onDeviceSelectionChange,
    onHoverDeviceId,
    timeRange: timeRangeFromProps,
    setTimeRange: setTimeRangeFromProps,
    isOgMode = false,
  }: Props
) {

  // Combine data from all devices for time range calculations and bounds checking
  // Sorted newest-first for efficient latest record access
  const data = useMemo(() => {
    return deviceDataResults
      .flatMap(r => r.data)
      .sort((a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      )
  }, [deviceDataResults])

  // Latest data timestamp per device — surfaced in the devices panel to show
  // how recently each sensor reported (sensors report ~1/min, so ages >>1min
  // mean a device has dropped offline). pyrmts returns records ascending, so
  // prefer `summary.latest` (already a max) and fall back to the last array
  // element to stay robust against ordering changes.
  const deviceLatestTimestamps = useMemo(() => {
    const map = new Map<number, Date | null>()
    for (const r of deviceDataResults) {
      const latestStr = r.summary?.latest ?? (r.data.length > 0 ? r.data[r.data.length - 1].timestamp : null)
      map.set(r.deviceId, latestStr ? new Date(latestStr) : null)
    }
    return map
  }, [deviceDataResults])

  // Y-axes state - combined primary + secondary + per-axis auto-range in URL (?y=tc, ?y=tca, ?y=tcaA)
  const metrics = useMetrics()
  const { l, r } = metrics

  // Device render strategy: how to visually distinguish multiple devices
  const [deviceRenderStrategy, setDeviceRenderStrategy] = useUrlState('dr', deviceRenderStrategyParam)

  // HSL config for hsv-nudge strategy
  const [hsvConfig, setHsvConfig] = useUrlState('hsl', hsvConfigParam)

  // X-axis grouping: auto mode (px values) or fixed window mode (time labels)
  const [xGrouping, setXGrouping] = useUrlState('x', xGroupingParam)

  // Custom range floors per metric (e.g., ?f=t32 for temp floor=32°F)
  const [rangeFloors, setRangeFloors] = useUrlState('f', rangeFloorsParam)

  // Smoothing: rolling average window size in minutes (0 = off)
  const [smoothing, setSmoothing] = useUrlState('s', smoothingParam)

  // Stddev band opacity (0-100%)
  const [stddevOpacity, setStddevOpacity] = useUrlState('so', stddevOpacityParam)

  // Raw line opacity when smoothing enabled (0-100%)
  const [rawOpacity, setRawOpacity] = useUrlState('ro', rawOpacityParam)

  // Helper to get effective floor (custom or default from metricConfig)
  const getEffectiveFloor = useCallback((metric: Metric) => {
    if (rangeFloors[metric] !== undefined) {
      return rangeFloors[metric]!
    }
    return getRangeFloor(metric)
  }, [rangeFloors])

  // Derive targetPx and overrideWindow from unified xGrouping state
  const targetPx = xGrouping.mode === 'auto' ? xGrouping.targetPx : null
  const overrideWindow = useMemo(() => {
    if (xGrouping.mode === 'fixed') {
      return TIME_WINDOWS.find(w => w.label === xGrouping.windowLabel)
    }
    return undefined
  }, [xGrouping])

  const isMobile = useMobile()
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth)

  // Legend hover state - tracks what is currently hovered
  const [hoverState, setHoverState] = useState<LegendHoverState>(null)

  // Auto-range button display state - what the buttons are currently showing (including preview)
  const [leftAutoRangeDisplay, setLeftAutoRangeDisplay] = useState(l.autoRange)
  const [rightAutoRangeDisplay, setRightAutoRangeDisplay] = useState(r.autoRange)

  // Device preview state - for showing what would happen if a device checkbox is toggled
  const [previewDeviceIds, setPreviewDeviceIds] = useState<number[] | null>(null)

  // Use preview device IDs when available, otherwise actual selected IDs
  const displayDeviceIds = previewDeviceIds ?? selectedDeviceIds

  // Refs for handling programmatic updates
  const ignoreNextRelayoutRef = useRef(false)
  const plotContainerRef = useRef<HTMLDivElement>(null)

  // Compute window size for buffer calculation (before useTimeRangeParam)
  const windowMinutes = useMemo(() => {
    return getWindowForDuration(timeRangeFromProps.duration, {
      containerWidth: viewportWidth,
      overrideWindow,
      targetPx,
    }).minutes
  }, [timeRangeFromProps.duration, viewportWidth, overrideWindow, targetPx])

  // Time range management - use props instead of internal hook
  const {
    xAxisRange,
    latestModeIntended,
    setXAxisRange,
    setLatestModeIntended,
    setDuration
  } = useTimeRangeParam(data, timeRangeFromProps, setTimeRangeFromProps, windowMinutes)

  // Metrics and Y-axis mode now persisted in URL params (via useUrlState above)

  // Time range handlers
  const handleTimeRangeClick = useCallback((hours: number) => {
    if (data.length === 0) return
    const duration = hours * 60 * 60 * 1000
    const latestTime = new Date(data[0].timestamp)
    const earliestTime = new Date(latestTime.getTime() - duration)
    const newRange: [string, string] = [formatForPlotly(earliestTime), formatForPlotly(latestTime)]
    setXAxisRange(newRange, { duration })
  }, [data, setXAxisRange])

  const setRangeByWidth = useCallback((hours: number, centerTime?: Date) => {
    if (data.length === 0) return
    const duration = hours * 60 * 60 * 1000
    const center = centerTime || (xAxisRange ? new Date((new Date(xAxisRange[0]).getTime() + new Date(xAxisRange[1]).getTime()) / 2) : new Date(data[0].timestamp))
    const halfRange = duration / 2
    const newStart = new Date(center.getTime() - halfRange)
    const newEnd = new Date(center.getTime() + halfRange)

    const newRange: [string, string] = [formatForPlotly(newStart), formatForPlotly(newEnd)]
    setXAxisRange(newRange, { duration })
  }, [xAxisRange, data, setXAxisRange])

  // Extract custom hooks - use multi-device aggregation
  const { deviceAggregations, selectedWindow, validWindows, isRawData } = useMultiDeviceAggregation(
    deviceDataResults,
    devices,
    xAxisRange,
    { containerWidth: viewportWidth, overrideWindow, targetPx },
    smoothing
  )

  // Filter device aggregations for display (when previewing a device checkbox toggle)
  const displayDeviceAggregations = useMemo(() => {
    return deviceAggregations.filter(d => displayDeviceIds.includes(d.deviceId))
  }, [deviceAggregations, displayDeviceIds])

  // Track which device to show in the DataTable (defaults to first device)
  const [selectedDeviceIdForTable, setSelectedDeviceIdForTable] = useState<number | undefined>(undefined)

  // Auto-select first device when displayDeviceAggregations changes, or reset if selected device no longer displayed
  useEffect(() => {
    if (displayDeviceAggregations.length === 0) return
    const selectedStillAvailable = displayDeviceAggregations.some(d => d.deviceId === selectedDeviceIdForTable)
    if (!selectedStillAvailable) {
      setSelectedDeviceIdForTable(displayDeviceAggregations[0].deviceId)
    }
  }, [displayDeviceAggregations, selectedDeviceIdForTable])

  // Table page size - persisted in URL
  const [tablePageSize, setTablePageSize] = useUrlState('p', intFromList([10, 20, 50, 100, 200] as const, 20))

  // Get the selected device's aggregated data for the table
  const selectedDeviceAggregation = displayDeviceAggregations.find(d => d.deviceId === selectedDeviceIdForTable)
  const aggregatedData = selectedDeviceAggregation?.aggregatedData || []

  // Calculate time range in minutes for aggregation control
  const timeRangeMinutes = useMemo(() => {
    if (xAxisRange) {
      const startTime = new Date(xAxisRange[0]).getTime()
      const endTime = new Date(xAxisRange[1]).getTime()
      return (endTime - startTime) / (1000 * 60)
    } else if (data.length > 1) {
      const firstTime = new Date(data[data.length - 1].timestamp).getTime()
      const lastTime = new Date(data[0].timestamp).getTime()
      return (lastTime - firstTime) / (1000 * 60)
    }
    return undefined
  }, [xAxisRange, data])

  const {
    autoUpdateRange,
    setIgnoreNextPanCheck
  } = useLatestMode(data, xAxisRange, latestModeIntended, setLatestModeIntended)

  // Helper: per-device earliest/latest derived from currently-fetched records.
  // Used by "Full History" + the table's data-range metadata.
  //
  // NB: this is bounded by what we've *already loaded* — a 1d view returns
  // 1d of bounds, so "Full History" from a narrow view will show whatever
  // the current fetch covered rather than the device's true earliest data.
  // Proper fix is wiring `earliestWatermarks` via a worker `/bounds` endpoint;
  // tracked separately. For most usage this is good enough since loading a
  // wider view first then Full-History'ing gives correct bounds.
  const getAllDeviceBounds = useCallback(() => {
    return deviceDataResults
      .filter(r => selectedDeviceIds.includes(r.deviceId) && r.data.length > 0)
      .map(r => {
        let earliest = r.data[0].timestamp
        let latest = r.data[0].timestamp
        for (const rec of r.data) {
          if (rec.timestamp < earliest) earliest = rec.timestamp
          if (rec.timestamp > latest) latest = rec.timestamp
        }
        return { earliest: new Date(earliest), latest: new Date(latest) }
      })
  }, [deviceDataResults, selectedDeviceIds])

  // Handle auto-update from Latest mode hook
  // Pass duration explicitly to avoid stale closure issues during concurrent updates
  useEffect(() => {
    if (autoUpdateRange) {
      setXAxisRange(autoUpdateRange, { duration: timeRangeFromProps.duration })
    }
  }, [autoUpdateRange, timeRangeFromProps.duration])

  // Determine which time range button is active based on the requested duration
  const getActiveTimeRange = useCallback(() => {
    // Use the requested duration from props, not the chart's x-axis range
    const durationHours = timeRangeFromProps.duration / (1000 * 60 * 60)
    const isLatestView = latestModeIntended

    // Check range width with tolerance
    if (abs(durationHours - 12) < 1) return isLatestView ? 'latest-12h' : '12h'
    if (abs(durationHours - 24) < 2) return isLatestView ? 'latest-1d' : '1d'
    if (abs(durationHours - (24 * 3)) < 6) return isLatestView ? 'latest-3d' : '3d'
    if (abs(durationHours - (24 * 7)) < 12) return isLatestView ? 'latest-7d' : '7d'
    if (abs(durationHours - (24 * 14)) < 24) return isLatestView ? 'latest-14d' : '14d'
    if (abs(durationHours - (24 * 31)) < 24) return isLatestView ? 'latest-1mo' : '1mo'
    if (abs(durationHours - (24 * 62)) < 48) return isLatestView ? 'latest-2mo' : '2mo'
    if (abs(durationHours - (24 * 92)) < 48) return isLatestView ? 'latest-3mo' : '3mo'

    // "All" is only active when duration exceeds 3 months
    if (durationHours > 24 * 100) return 'all'

    return isLatestView ? 'latest-custom' : 'custom'
  }, [timeRangeFromProps.duration, latestModeIntended])

  // Double click handler
  const handleDoubleClick = useCallback(() => {
    if (data.length > 0) {
      ignoreNextRelayoutRef.current = true
      const fullRange: [string, string] = [
        formatForPlotly(new Date(data[data.length - 1].timestamp)),
        formatForPlotly(new Date(data[0].timestamp))
      ]
      setXAxisRange(fullRange)
    } else {
      setXAxisRange(null)
    }
  }, [data])

  // Dismiss hover tooltip when clicking outside plot area
  const setupHoverDismiss = usePlotlyHoverDismiss(plotContainerRef as React.RefObject<HTMLDivElement>)

  // "All" handler - show full data extent from file bounds
  const handleAllClick = useCallback(() => {
    const allBounds = getAllDeviceBounds()
    if (allBounds.length === 0) return

    // Find overall earliest and latest across all devices
    const earliest = allBounds.reduce((min, b) => b.earliest < min ? b.earliest : min, allBounds[0].earliest)
    const latest = allBounds.reduce((max, b) => b.latest > max ? b.latest : max, allBounds[0].latest)
    const durationMs = latest.getTime() - earliest.getTime()

    // Set the visual range (anchored to current time in Latest mode)
    const BUFFER_MS = 60 * 1000 // 1 minute buffer
    const endTime = new Date(new Date().getTime() + BUFFER_MS)
    const startTime = new Date(endTime.getTime() - durationMs)
    setXAxisRange([formatForPlotly(startTime), formatForPlotly(endTime)])

    // Use Latest mode (timestamp=null) so it auto-follows new data, but with full duration
    setTimeRangeFromProps({ timestamp: null, duration: durationMs })
  }, [getAllDeviceBounds, setXAxisRange, setTimeRangeFromProps])

  // Relayout handler
  const handleRelayout = useCallback((eventData: PlotRelayoutEvent) => {
    const x0 = eventData['xaxis.range[0]']
    const x1 = eventData['xaxis.range[1]']
    if (x0 !== undefined && x1 !== undefined) {
      // PlotRelayoutEvent types these as number, but for date axes they're strings
      const newStart = new Date(x0 as unknown as string)
      const newEnd = new Date(x1 as unknown as string)
      const newDuration = newEnd.getTime() - newStart.getTime()

      // Check if range end goes into the future
      const allBounds = getAllDeviceBounds()
      if (allBounds.length > 0) {
        const absoluteLatest = allBounds.reduce((max, b) => b.latest > max ? b.latest : max, allBounds[0].latest)
        const FUTURE_BUFFER = 2 * 60 * 1000 // 2 minutes

        if (newEnd.getTime() > absoluteLatest.getTime() + FUTURE_BUFFER) {
          // End is in future - clamp to latest but keep the new duration
          // This happens when zooming out in Latest mode
          setXAxisRange(null, { duration: newDuration }) // null = Latest mode
          return
        }
      }

      setXAxisRange([formatForPlotly(newStart), formatForPlotly(newEnd)], { duration: newDuration })
    }
  }, [setXAxisRange, getAllDeviceBounds])

  // Helper: set primary metric (swapping if needed)
  const setLeftMetric = useCallback((metric: Metric) => {
    l.set(metric)
    if (r.val === metric) r.set('none')
  }, [l, r])

  // Helper: set secondary metric (swapping if needed)
  const setRightMetric = useCallback((metric: Metric) => {
    if (metric === l.val && r.val !== 'none') {
      l.set(r.val as typeof metric)
      r.set(metric)
    } else if (metric !== l.val) {
      r.set(metric)
    }
  }, [l, r])

  // Helper: toggle device by name pattern
  const toggleDeviceByPattern = useCallback((pattern: string) => {
    const regex = new RegExp(pattern, 'i')
    const device = devices.find(d => regex.test(d.name))
    if (!device) return
    const deviceId = device.deviceId
    if (selectedDeviceIds.includes(deviceId)) {
      if (selectedDeviceIds.length > 1) {
        onDeviceSelectionChange(selectedDeviceIds.filter(id => id !== deviceId))
      }
    } else {
      onDeviceSelectionChange([...selectedDeviceIds, deviceId])
    }
  }, [devices, selectedDeviceIds, onDeviceSelectionChange])

  // Helper: toggle latest mode
  const toggleLatestMode = useCallback(() => {
    if (latestModeIntended) {
      setLatestModeIntended(false)
    } else if (xAxisRange && data.length > 0) {
      const rangeStart = new Date(xAxisRange[0])
      const rangeEnd = new Date(xAxisRange[1])
      const currentWidth = rangeEnd.getTime() - rangeStart.getTime()
      const latestTime = new Date(data[0].timestamp)
      const newStart = new Date(latestTime.getTime() - currentWidth)
      setIgnoreNextPanCheck()
      setXAxisRange([formatForPlotly(newStart), formatForPlotly(latestTime)])
      setLatestModeIntended(true)
    }
  }, [latestModeIntended, setLatestModeIntended, xAxisRange, data, setIgnoreNextPanCheck, setXAxisRange])

  // ===== Register actions with useAction =====

  // Left Y-axis metrics
  useAction('left:temp', { label: 'Temperature', group: 'Y-Axis Metrics', defaultBindings: ['t'], handler: () => setLeftMetric('temp') })
  useAction('left:co2', { label: 'CO₂', group: 'Y-Axis Metrics', defaultBindings: ['c'], handler: () => setLeftMetric('co2') })
  useAction('left:humid', { label: 'Humidity', group: 'Y-Axis Metrics', defaultBindings: ['h'], handler: () => setLeftMetric('humid') })
  useAction('left:pm25', { label: 'PM2.5', group: 'Y-Axis Metrics', defaultBindings: ['p'], handler: () => setLeftMetric('pm25') })
  useAction('left:voc', { label: 'VOC', group: 'Y-Axis Metrics', defaultBindings: ['v'], handler: () => setLeftMetric('voc') })
  useAction('left:autorange', { label: 'Toggle auto-range', group: 'Y-Axis Metrics', defaultBindings: ['a'], handler: () => l.setAutoRange(!l.autoRange) })

  // Right Y-axis metrics
  useAction('right:temp', { label: 'Temperature', group: 'Y-Axis Metrics', defaultBindings: ['shift+t'], handler: () => setRightMetric('temp') })
  useAction('right:co2', { label: 'CO₂', group: 'Y-Axis Metrics', defaultBindings: ['shift+c'], handler: () => setRightMetric('co2') })
  useAction('right:humid', { label: 'Humidity', group: 'Y-Axis Metrics', defaultBindings: ['shift+h'], handler: () => setRightMetric('humid') })
  useAction('right:pm25', { label: 'PM2.5', group: 'Y-Axis Metrics', defaultBindings: ['shift+p'], handler: () => setRightMetric('pm25') })
  useAction('right:voc', { label: 'VOC', group: 'Y-Axis Metrics', defaultBindings: ['shift+v'], handler: () => setRightMetric('voc') })
  useAction('right:none', { label: 'Clear', group: 'Y-Axis Metrics', defaultBindings: ['shift+n'], handler: () => r.set('none') })
  useAction('right:autorange', { label: 'Toggle auto-range', group: 'Y-Axis Metrics', defaultBindings: ['shift+a'], handler: () => { if (r.val !== 'none') r.setAutoRange(!r.autoRange) } })

  // Time ranges - dynamic with digit wildcards
  useAction('time:hours', { label: 'Range: N hours', group: 'Time Range', defaultBindings: ['\\d+ h', 'h \\d+'], keywords: ['hours'], handler: useCallback((_, captures) => handleTimeRangeClick(captures?.[0] ?? 1), [handleTimeRangeClick]) })
  useAction('time:days', { label: 'Range: N days', group: 'Time Range', defaultBindings: ['\\d+ d', 'd \\d+'], keywords: ['days'], handler: useCallback((_, captures) => handleTimeRangeClick((captures?.[0] ?? 1) * 24), [handleTimeRangeClick]) })
  useAction('time:weeks', { label: 'Range: N weeks', group: 'Time Range', defaultBindings: ['\\d+ w', 'w \\d+'], keywords: ['weeks'], handler: useCallback((_, captures) => handleTimeRangeClick((captures?.[0] ?? 1) * 24 * 7), [handleTimeRangeClick]) })
  useAction('time:months', { label: 'Range: N months', group: 'Time Range', defaultBindings: ['\\d+ m', 'm \\d+'], keywords: ['months'], handler: useCallback((_, captures) => handleTimeRangeClick((captures?.[0] ?? 1) * 24 * 31), [handleTimeRangeClick]) })
  useAction('time:years', { label: 'Range: N years', group: 'Time Range', defaultBindings: ['\\d+ y', 'y \\d+'], keywords: ['years'], handler: useCallback((_, captures) => handleTimeRangeClick((captures?.[0] ?? 1) * 24 * 365), [handleTimeRangeClick]) })
  // Special time ranges (not digit-based)
  useAction('time:all', { label: 'Full history', group: 'Time Range', defaultBindings: ['f'], keywords: ['all', 'everything', 'max', 'full'], handler: handleAllClick })
  useAction('time:latest', { label: 'Latest', group: 'Time Range', defaultBindings: ['l'], keywords: ['now', 'current', 'live'], handler: toggleLatestMode })

  // Devices (dynamic from device list)
  const deviceActions = useMemo(() => {
    const actions: Record<string, { label: string; group: string; defaultBindings: string[]; keywords?: string[]; handler: () => void }> = {}
    for (const device of devices) {
      const name = device.name
      const key = name.toLowerCase()
      actions[`device:${key}`] = {
        label: name,
        group: 'Toggle devices on/off',
        defaultBindings: [key[0]],
        handler: () => toggleDeviceByPattern(key),
      }
    }
    return actions
  }, [devices, toggleDeviceByPattern])
  useActions(deviceActions)

  // X-axis grouping (aggregation)
  useAction('agg:1px', { label: '1px', group: 'X Grouping', defaultBindings: ['x 1'], keywords: ['1px', 'auto', 'aggregation'], handler: () => setXGrouping({ mode: 'auto', targetPx: 1 }) })
  useAction('agg:2px', { label: '2px', group: 'X Grouping', defaultBindings: ['x 2'], keywords: ['2px', 'auto', 'aggregation'], handler: () => setXGrouping({ mode: 'auto', targetPx: 2 }) })
  useAction('agg:4px', { label: '4px', group: 'X Grouping', defaultBindings: ['x 4'], keywords: ['4px', 'auto', 'aggregation'], handler: () => setXGrouping({ mode: 'auto', targetPx: 4 }) })
  useAction('agg:8px', { label: '8px', group: 'X Grouping', defaultBindings: ['x 8'], keywords: ['8px', 'auto', 'aggregation'], handler: () => setXGrouping({ mode: 'auto', targetPx: 8 }) })

  // Smoothing (rolling average) - accepts any value, not just presets
  useAction('smooth:minutes', { label: 'Smooth: N minutes', group: 'Smoothing', defaultBindings: ['\\d+ M', 'M \\d+'], keywords: ['smooth', 'rolling', 'average'], handler: useCallback((_, captures) => {
    setSmoothing(captures?.[0] ?? 5)
  }, [setSmoothing]) })

  useAction('smooth:hours', { label: 'Smooth: N hours', group: 'Smoothing', defaultBindings: ['\\d+ H', 'H \\d+'], keywords: ['smooth', 'rolling', 'average'], handler: useCallback((_, captures) => {
    setSmoothing((captures?.[0] ?? 1) * 60)
  }, [setSmoothing]) })

  useAction('smooth:days', { label: 'Smooth: N days', group: 'Smoothing', defaultBindings: ['\\d+ D', 'D \\d+'], keywords: ['smooth', 'rolling', 'average'], handler: useCallback((_, captures) => {
    setSmoothing((captures?.[0] ?? 1) * 1440)
  }, [setSmoothing]) })

  useAction('smooth:off', { label: 'Smooth: Off', group: 'Smoothing', defaultBindings: ['0 M', 'O'], keywords: ['smooth', 'off', 'none', 'disable'], handler: () => setSmoothing(1) })

  useAction('smooth:auto', { label: 'Smooth: Auto', group: 'Smoothing', defaultBindings: ['M A', 'A M'], keywords: ['smooth', 'auto', 'adaptive'], handler: () => setSmoothing(SMOOTHING_AUTO) })

  // Dynamic page title: "Gym BR 🌡️ 💦" format
  useEffect(() => {
    const metricEmoji: Record<Metric | 'none', string> = {
      temp: '🌡️',
      co2: '💨',
      humid: '💦',
      pm25: '🌫️',
      voc: '🧪',
      none: '',
    }

    // Get short device names (use device name directly, should already be short like "Gym", "BR")
    const deviceNames = selectedDeviceIds
      .map(id => devices.find(d => d.deviceId === id)?.name)
      .filter(Boolean)
      .join(' ')

    // Get metric emojis (left first, then right if different)
    const leftEmoji = metricEmoji[l.val]
    const rightEmoji = r.val !== 'none' && r.val !== l.val ? metricEmoji[r.val] : ''

    const emojis = [leftEmoji, rightEmoji].filter(Boolean).join('')
    const title = [deviceNames, emojis].filter(Boolean).join(' ') || 'Awair'
    document.title = title
  }, [selectedDeviceIds, devices, l.val, r.val])

  // Track viewport width for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('orientationchange', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      window.removeEventListener('orientationchange', handleResize)
    }
  }, [])

  // Gate scroll zoom behind meta+scroll to avoid accidental zooms while scrolling page
  useEffect(() => {
    const container = plotContainerRef.current
    if (!container) return

    const handleWheel = (e: WheelEvent) => {
      // Only allow scroll zoom if meta key is pressed
      if (!e.metaKey && !e.ctrlKey) {
        e.stopPropagation()
      }
    }

    // Use capture phase to intercept before plotly sees it
    container.addEventListener('wheel', handleWheel, { capture: true })
    return () => container.removeEventListener('wheel', handleWheel, { capture: true })
  }, [])

  // Theme-aware plot colors (pltly watches data-theme + prefers-color-scheme)
  const { isDark } = usePlotTheme({
    cssVars: {
      grid: '--plot-grid',
      bg: '--plot-bg',
      font: '--text-primary',
    },
  })
  const plotColors = useMemo(() => ({
    gridcolor: getComputedStyle(document.documentElement).getPropertyValue('--plot-grid').trim() || '#ddd',
    plotBg: getComputedStyle(document.documentElement).getPropertyValue('--plot-bg').trim() || 'white',
    legendBg: getComputedStyle(document.documentElement).getPropertyValue('--bg-secondary').trim() || 'white',
    textColor: getComputedStyle(document.documentElement).getPropertyValue('--text-primary').trim() || '#333',
    spikeColor: isDark ? 'rgb(255, 255, 255)' : 'rgb(0, 0, 0)',
  }), [isDark])

  // Custom tick formatting via pltly. pltly's `generateDateTicks` caps its
  // interval ladder at 1 day — for wider ranges it'd emit one tick per day
  // and Plotly shows them all overlapping. Above ~7 days, hand off to
  // Plotly's built-in auto-date-ticker, which handles week/month/year
  // stepping correctly out of the box.
  const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000
  const { tickvals, ticktext } = useMemo(() => {
    if (!xAxisRange || data.length === 0) return { tickvals: [], ticktext: [] }
    const rangeMs = new Date(xAxisRange[1]).getTime() - new Date(xAxisRange[0]).getTime()
    if (rangeMs > SEVEN_DAYS_MS) return { tickvals: [], ticktext: [] }
    const plotWidth = max(300, viewportWidth - 100)
    return generateDateTicks({
      range: [new Date(xAxisRange[0]), new Date(xAxisRange[1])],
      containerWidth: plotWidth,
    })
  }, [xAxisRange, data, viewportWidth])

  // Plot data preparation
  const config = metricConfig[l.val] || metricConfig.temp
  const secondaryConfig = r.val !== 'none' ? metricConfig[r.val] : null
  const totalDevices = displayDeviceAggregations.length

  // Helper to calculate trace opacity based on hover state
  const getTraceOpacity = useCallback((deviceIdx: number, metric: 'primary' | 'secondary'): number => {
    if (!hoverState) return 1.0
    if (hoverState.type === 'device') {
      return hoverState.deviceIdx === deviceIdx ? 1.0 : 0.2
    }
    if (hoverState.type === 'trace') {
      return hoverState.deviceIdx === deviceIdx && hoverState.metric === metric ? 1.0 : 0.2
    }
    // hoverState.type === 'metric'
    return hoverState.metric === metric ? 1.0 : 0.2
  }, [hoverState])

  // Convert opacity percent (0-100) to 2-char hex (00-ff)
  const opacityHex = useMemo(() => {
    const value = Math.round((stddevOpacity / 100) * 255)
    return value.toString(16).padStart(2, '0')
  }, [stddevOpacity])

  // Check if smoothing is enabled (any device has smoothedData)
  const hasSmoothing = displayDeviceAggregations.some(d => d.smoothedData !== null)

  // Generate traces for all devices, grouped by metric for better hover ordering
  const plotTraces = useMemo(() => {
    const traces: DataWithZorder[] = []

    // Pre-compute data for all devices (raw and smoothed)
    const deviceData = displayDeviceAggregations.map((deviceAgg, deviceIdx) => {
      const { aggregatedData: rawData, smoothedData, deviceName, deviceId } = deviceAgg
      const timestamps = rawData.map(d => formatForPlotly(new Date(d.timestamp)))
      const smoothedTimestamps = smoothedData?.map(d => formatForPlotly(new Date(d.timestamp))) ?? []

      // Line width: thin for raw when smoothing, normal otherwise
      const rawLineWidth = hasSmoothing ? 1 : 2
      const smoothedLineWidth = 3

      // Get line props for primary metric (raw data)
      const primaryLineProps = getDeviceLineProps(
        config.color,
        deviceIdx,
        totalDevices,
        deviceRenderStrategy,
        rawLineWidth,
        hsvConfig
      )

      // Get line props for smoothed overlay (thicker)
      const primarySmoothedLineProps = hasSmoothing ? getDeviceLineProps(
        config.color,
        deviceIdx,
        totalDevices,
        deviceRenderStrategy,
        smoothedLineWidth,
        hsvConfig
      ) : null

      // Primary metric - raw data
      const avgValues = rawData.map(d => d[`${l.val}_avg` as keyof typeof d] as number | null)
      const stddevValues = rawData.map(d => d[`${l.val}_stddev` as keyof typeof d] as number | null)

      // Primary metric - smoothed data (for overlay and stddev bands)
      const smoothedAvgValues = smoothedData?.map(d => d[`${l.val}_avg` as keyof typeof d] as number | null) ?? []
      const smoothedStddevValues = smoothedData?.map(d => d[`${l.val}_stddev` as keyof typeof d] as number | null) ?? []
      // Propagate nulls: null + anything = null (avoid JS's null + null = 0)
      const upperValues = smoothedAvgValues.map((avg, i) =>
        avg === null || smoothedStddevValues[i] === null ? null : avg + smoothedStddevValues[i]
      )
      const lowerValues = smoothedAvgValues.map((avg, i) =>
        avg === null || smoothedStddevValues[i] === null ? null : avg - smoothedStddevValues[i]
      )

      // Secondary metric - raw data
      const secondaryAvgValues = secondaryConfig && r.val !== 'none'
        ? rawData.map(d => d[`${r.val}_avg` as keyof typeof d] as number | null)
        : []
      const secondaryStddevValues = secondaryConfig && r.val !== 'none'
        ? rawData.map(d => d[`${r.val}_stddev` as keyof typeof d] as number | null)
        : []

      // Secondary metric - smoothed data
      const secondarySmoothedAvgValues = secondaryConfig && smoothedData
        ? smoothedData.map(d => d[`${r.val}_avg` as keyof typeof d] as number | null)
        : []
      const secondarySmoothedStddevValues = secondaryConfig && smoothedData
        ? smoothedData.map(d => d[`${r.val}_stddev` as keyof typeof d] as number | null)
        : []
      const secondaryUpperValues = secondarySmoothedAvgValues.map((avg, i) =>
        avg === null || secondarySmoothedStddevValues[i] === null ? null : avg + secondarySmoothedStddevValues[i]
      )
      const secondaryLowerValues = secondarySmoothedAvgValues.map((avg, i) =>
        avg === null || secondarySmoothedStddevValues[i] === null ? null : avg - secondarySmoothedStddevValues[i]
      )

      // Get line props for secondary metric
      const secondaryLineProps = secondaryConfig
        ? getDeviceLineProps(
          secondaryConfig.color,
          deviceIdx,
          totalDevices,
          deviceRenderStrategy,
          rawLineWidth,
          hsvConfig
        )
        : null

      const secondarySmoothedLineProps = secondaryConfig && hasSmoothing
        ? getDeviceLineProps(
          secondaryConfig.color,
          deviceIdx,
          totalDevices,
          deviceRenderStrategy,
          smoothedLineWidth,
          hsvConfig
        )
        : null

      // For multi-device mode, use compact device names in legend
      const legendName = totalDevices > 1 ? deviceName : ''

      return {
        rawData, smoothedData, deviceName, deviceId, deviceIdx, timestamps, smoothedTimestamps, legendName,
        primaryLineProps, primarySmoothedLineProps, avgValues, stddevValues, smoothedAvgValues, smoothedStddevValues, upperValues, lowerValues,
        secondaryLineProps, secondarySmoothedLineProps, secondaryAvgValues, secondaryStddevValues, secondarySmoothedAvgValues, secondarySmoothedStddevValues, secondaryUpperValues, secondaryLowerValues,
      }
    })

    // Stable `uid` on every trace — Plotly's d3 v7 data join uses `trace.uid`
    // as the key function; without a caller-supplied uid, Plotly falls back to
    // index-based random uid reuse, which on trace add/remove leaves stale
    // `g.trace` DOM nodes behind (duplicate rendered traces after toggling a
    // device off). Keying by metric/deviceId/role makes each trace identity
    // stable across renders so d3's exit selection removes the right nodes.
    // Separator must be CSS-selector-safe: Plotly generates class selectors
    // like `.cb{uid}` and chokes on `:` / `.` / other special chars.
    const uid = (parts: (string | number)[]): string => parts.join('-')

    // Synthetic header trace for primary metric (invisible line, provides metric header in hover)
    if (deviceData.length > 0) {
      const d = deviceData[0]
      traces.push({
        x: d.timestamps,
        y: d.avgValues,  // Use real y-values so hover triggers
        mode: 'lines',
        line: { color: 'rgba(0,0,0,0)', width: 0 },
        showlegend: false,
        hovertemplate: `<b>${config.label} (${config.unit})</b><extra></extra>`,
        uid: uid(['header', 'primary', l.val]),
      } as DataWithZorder)
    }

    // PRIMARY METRIC traces for all devices
    // When smoothing: thin raw lines (reduced opacity) + thick smoothed overlay
    // Without smoothing: normal lines
    deviceData.forEach((d) => {
      // Raw data trace (thin when smoothing enabled)
      traces.push({
        x: d.timestamps,
        y: d.avgValues,
        mode: 'lines',
        line: d.primaryLineProps,
        opacity: hasSmoothing ? (rawOpacity / 100) * getTraceOpacity(d.deviceIdx, 'primary') : getTraceOpacity(d.deviceIdx, 'primary'),
        name: d.legendName || `${config.label} (${config.unit})`,
        legendgroup: 'primary',
        showlegend: !hasSmoothing,  // Hide legend for raw when smoothing (smoothed trace shows legend)
        uid: uid(['raw', 'primary', l.val, d.deviceId]),
        ...(isRawData ? {
          hovertemplate: `${d.deviceName}: %{y:.1f}<extra></extra>`
        } : hasSmoothing ? {
          hoverinfo: 'skip' as const,  // Skip hover on raw when showing smoothed overlay
        } : {
          customdata: d.rawData.map((rec, i) => ([
            d.stddevValues[i],
            rec.count
          ])),
          hovertemplate: `${d.deviceName}: %{y:.1f} ±%{customdata[0]:.1f} (n=%{customdata[1]})<extra></extra>`
        })
      } as DataWithZorder)

      // Smoothed overlay trace (thick) - only when smoothing enabled
      if (d.smoothedData && d.primarySmoothedLineProps) {
        traces.push({
          x: d.smoothedTimestamps,
          y: d.smoothedAvgValues,
          mode: 'lines',
          line: d.primarySmoothedLineProps,
          opacity: getTraceOpacity(d.deviceIdx, 'primary'),
          name: d.legendName || `${config.label} (${config.unit})`,
          legendgroup: 'primary',
          customdata: d.smoothedData.map((rec, i) => ([
            d.smoothedStddevValues[i],
            rec.count
          ])),
          hovertemplate: `${d.deviceName}: %{y:.1f} ±%{customdata[0]:.1f} (n=%{customdata[1]})<extra></extra>`,
          uid: uid(['smoothed', 'primary', l.val, d.deviceId]),
        } as DataWithZorder)
      }
    })

    // Synthetic header trace for secondary metric (invisible line, provides metric header in hover)
    if (secondaryConfig && deviceData.length > 0) {
      const d = deviceData[0]
      traces.push({
        x: d.timestamps,
        y: d.secondaryAvgValues,  // Use real y-values so hover triggers
        mode: 'lines',
        line: { color: 'rgba(0,0,0,0)', width: 0 },
        showlegend: false,
        uid: uid(['header', 'secondary', r.val]),
        yaxis: 'y2',
        hovertemplate: `<b>${secondaryConfig.label} (${secondaryConfig.unit})</b><extra></extra>`
      })
    }

    // SECONDARY METRIC traces for all devices
    if (secondaryConfig) {
      deviceData.forEach((d) => {
        if (d.secondaryLineProps) {
          // Raw data trace
          traces.push({
            x: d.timestamps,
            y: d.secondaryAvgValues,
            mode: 'lines',
            line: d.secondaryLineProps,
            opacity: hasSmoothing ? (rawOpacity / 100) * getTraceOpacity(d.deviceIdx, 'secondary') : getTraceOpacity(d.deviceIdx, 'secondary'),
            name: d.legendName || `${secondaryConfig.label} (${secondaryConfig.unit})`,
            legendgroup: 'secondary',
            legend: 'legend2',
            yaxis: 'y2',
            showlegend: !hasSmoothing,
            uid: uid(['raw', 'secondary', r.val, d.deviceId]),
            ...(isRawData ? {
              hovertemplate: `${d.deviceName}: %{y:.1f}<extra></extra>`
            } : hasSmoothing ? {
              hoverinfo: 'skip' as const,
            } : {
              customdata: d.rawData.map((rec, i) => ([
                d.secondaryStddevValues[i],
                rec.count
              ])),
              hovertemplate: `${d.deviceName}: %{y:.1f} ±%{customdata[0]:.1f} (n=%{customdata[1]})<extra></extra>`
            })
          } as DataWithZorder)

          // Smoothed overlay trace
          if (d.smoothedData && d.secondarySmoothedLineProps) {
            traces.push({
              x: d.smoothedTimestamps,
              y: d.secondarySmoothedAvgValues,
              mode: 'lines',
              line: d.secondarySmoothedLineProps,
              opacity: getTraceOpacity(d.deviceIdx, 'secondary'),
              name: d.legendName || `${secondaryConfig.label} (${secondaryConfig.unit})`,
              legendgroup: 'secondary',
              legend: 'legend2',
              yaxis: 'y2',
              customdata: d.smoothedData.map((rec, i) => ([
                d.secondarySmoothedStddevValues[i],
                rec.count
              ])),
              hovertemplate: `${d.deviceName}: %{y:.1f} ±%{customdata[0]:.1f} (n=%{customdata[1]})<extra></extra>`,
              uid: uid(['smoothed', 'secondary', r.val, d.deviceId]),
            } as DataWithZorder)
          }
        }
      })
    }

    // Stddev fill regions (±σ shaded areas) - from smoothed data when available
    // Split into segments at gaps (null values) so Plotly doesn't interpolate across gaps
    if (!isRawData || hasSmoothing) {
      deviceData.forEach(d => {
        // Primary stddev bands
        const bandTimestamps = hasSmoothing ? d.smoothedTimestamps : d.timestamps
        const bandUpper = hasSmoothing ? d.upperValues : d.avgValues.map((avg, i) =>
          avg === null || d.stddevValues[i] === null ? null : avg + d.stddevValues[i]
        )
        const bandLower = hasSmoothing ? d.lowerValues : d.avgValues.map((avg, i) =>
          avg === null || d.stddevValues[i] === null ? null : avg - d.stddevValues[i]
        )

        const segments = splitBandSegments(bandTimestamps, bandUpper, bandLower)
        segments.forEach((seg, segIdx) => {
          traces.push({
            x: seg.timestamps,
            y: seg.lower,
            mode: 'lines',
            line: { color: 'transparent' },
            name: `${config.label} Lower`,
            showlegend: false,
            hoverinfo: 'skip',
            uid: uid(['stddev', 'primary', l.val, d.deviceId, 'lower', segIdx]),
          } as DataWithZorder)
          traces.push({
            x: seg.timestamps,
            y: seg.upper,
            fill: 'tonexty',
            fillcolor: `${d.primaryLineProps.color}${opacityHex}`,
            line: { color: 'transparent' },
            mode: 'lines',
            name: segIdx === 0 ? `±σ ${config.label}` : `±σ ${config.label} (cont)`,
            showlegend: false,
            hoverinfo: 'skip',
            uid: uid(['stddev', 'primary', l.val, d.deviceId, 'upper', segIdx]),
          } as DataWithZorder)
        })

        // Secondary stddev bands
        if (secondaryConfig) {
          const secBandTimestamps = hasSmoothing ? d.smoothedTimestamps : d.timestamps
          const secBandUpper = hasSmoothing ? d.secondaryUpperValues : d.secondaryAvgValues.map((avg, i) =>
            avg === null || d.secondaryStddevValues[i] === null ? null : avg + d.secondaryStddevValues[i]
          )
          const secBandLower = hasSmoothing ? d.secondaryLowerValues : d.secondaryAvgValues.map((avg, i) =>
            avg === null || d.secondaryStddevValues[i] === null ? null : avg - d.secondaryStddevValues[i]
          )

          const secSegments = splitBandSegments(secBandTimestamps, secBandUpper, secBandLower)
          secSegments.forEach((seg, segIdx) => {
            traces.push({
              x: seg.timestamps,
              y: seg.lower,
              mode: 'lines',
              line: { color: 'transparent' },
              name: `${secondaryConfig.label} Lower`,
              showlegend: false,
              hoverinfo: 'skip',
              yaxis: 'y2',
              uid: uid(['stddev', 'secondary', r.val, d.deviceId, 'lower', segIdx]),
            } as DataWithZorder)
            traces.push({
              x: seg.timestamps,
              y: seg.upper,
              fill: 'tonexty',
              fillcolor: `${d.secondaryLineProps?.color}${opacityHex}`,
              line: { color: 'transparent' },
              mode: 'lines',
              name: segIdx === 0 ? `±σ ${secondaryConfig.label}` : `±σ ${secondaryConfig.label} (cont)`,
              showlegend: false,
              hoverinfo: 'skip',
              yaxis: 'y2',
              uid: uid(['stddev', 'secondary', r.val, d.deviceId, 'upper', segIdx]),
            } as DataWithZorder)
          })
        }
      })
    }

    return traces
  }, [displayDeviceAggregations, l.val, r.val, config, secondaryConfig, totalDevices, isRawData, hasSmoothing, rawOpacity, deviceRenderStrategy, hsvConfig, getTraceOpacity, opacityHex])

  // Font sizes - larger in og mode for better screenshot readability
  // Note: x-axis ticks need smaller font to fit with multi-line date labels
  const fontSizes = isOgMode
    ? { tick: 14, legend: 22, annotation: 24, title: 28 }
    : { tick: 11, legend: 11, annotation: 12, title: 16 }

  // Helper to create yaxis config
  const createYAxisConfig = (side: 'left' | 'right', autoRange: boolean, floor: number = 0) => ({
    gridcolor: side === 'left' ? plotColors.gridcolor : 'transparent',
    fixedrange: true,
    tickfont: { color: plotColors.textColor, size: fontSizes.tick },
    linecolor: plotColors.gridcolor,
    zerolinecolor: side === 'left' ? plotColors.gridcolor : 'transparent',
    side,
    tickformat: '.3~s',
    // If not auto-ranging: use floor as minimum (default 0 with rangemode tozero)
    ...(!autoRange && floor === 0 && { rangemode: 'tozero' as const }),
    // For metrics with floor > 0 (e.g., CO2 at 400ppm), use autorange:'max' to auto-scale upper bound
    ...(!autoRange && floor > 0 && { autorange: 'max' as const, range: [floor, null] }),
    ...(side === 'right' && { overlaying: 'y' as const }),
  })
  // OG mode: fill viewport height (625px to leave room for bottom margin in 630px viewport)
  const chartHeight = isOgMode ? 625 : (isMobile ? 300 : 500)

  // Consolidate table metadata calculations
  const tableMetadata = useMemo(() => {
    if (!selectedDeviceIdForTable) {
      return { totalDataCount: 0, rawDataCount: 0, fullDataStartTime: undefined, fullDataEndTime: undefined }
    }

    const deviceResult = deviceDataResults.find(r => r.deviceId === selectedDeviceIdForTable)
    if (!deviceResult || deviceResult.data.length === 0) {
      return { totalDataCount: 0, rawDataCount: 0, fullDataStartTime: undefined, fullDataEndTime: undefined }
    }
    let earliest = deviceResult.data[0].timestamp
    let latest = deviceResult.data[0].timestamp
    for (const rec of deviceResult.data) {
      if (rec.timestamp < earliest) earliest = rec.timestamp
      if (rec.timestamp > latest) latest = rec.timestamp
    }
    const earliestD = new Date(earliest)
    const latestD = new Date(latest)
    const totalMinutes = (latestD.getTime() - earliestD.getTime()) / (1000 * 60)
    const bounds = { earliest: earliestD, latest: latestD }
    return {
      totalDataCount: ceil(totalMinutes / selectedWindow.minutes),
      rawDataCount: ceil(totalMinutes),  // ~1 data point per minute
      fullDataStartTime: bounds.earliest,
      fullDataEndTime: bounds.latest,
    }
  }, [selectedDeviceIdForTable, selectedWindow, deviceDataResults])

  return (
    <div className={`awair-chart${isOgMode ? ' og-mode' : ''}`}>
      {/* OG mode title overlay */}
      {isOgMode && (
        <div className="og-title" style={{ color: plotColors.textColor }}>
          <span>Air Quality Dashboard</span>
          <span className="emojis">🌡️ 💨 💦 🏭 🧪</span>
        </div>
      )}
      <div
        ref={plotContainerRef}
        className="plot-container"
        onMouseEnter={() => setHoverState(null)}
      >
        {(() => {
          // Calculate device colors for legend markers (primary metric)
          const primaryColors = displayDeviceAggregations.map((_, deviceIdx) => {
            const lineProps = getDeviceLineProps(
              config.color,
              deviceIdx,
              totalDevices,
              deviceRenderStrategy,
              2,
              hsvConfig
            )
            return lineProps.color
          })
          // Calculate device colors for secondary metric
          const secondaryColors = secondaryConfig
            ? displayDeviceAggregations.map((_, deviceIdx) => {
              const lineProps = getDeviceLineProps(
                secondaryConfig.color,
                deviceIdx,
                totalDevices,
                deviceRenderStrategy,
                2,
                hsvConfig
              )
              return lineProps.color
            })
            : []
          return (
            <CustomLegend
              metrics={metrics}
              isMobile={isMobile}
              deviceNames={displayDeviceAggregations.map(d => d.deviceName)}
              primaryColors={primaryColors}
              secondaryColors={secondaryColors}
              onHover={isOgMode ? noop : setHoverState}
              onLeftAutoRangeDisplayChange={isOgMode ? noop : setLeftAutoRangeDisplay}
              onRightAutoRangeDisplayChange={isOgMode ? noop : setRightAutoRangeDisplay}
              getEffectiveFloor={getEffectiveFloor}
              rangeFloors={rangeFloors}
              setRangeFloors={setRangeFloors}
            />
          )
        })()}
        <Plot
          data={plotTraces}
          layout={{
            autosize: true,
            height: chartHeight,
            uirevision: 'stable',
            xaxis: {
              type: 'date',
              autorange: !xAxisRange,
              ...(xAxisRange && { range: xAxisRange }),
              ...(data.length > 0 && {
                rangeslider: { visible: false },
                constraintoward: 'center',
              }),
              gridcolor: plotColors.gridcolor,
              tickfont: { color: plotColors.textColor, size: fontSizes.tick },
              linecolor: plotColors.gridcolor,
              zerolinecolor: plotColors.gridcolor,
              hoverformat: '',
              showspikes: true,
              spikemode: 'across',
              spikethickness: 0.5,
              spikecolor: plotColors.spikeColor,
              spikedash: 'solid',
              ...({ unifiedhovertitle: { text: '%{x|%b %-d, %-I:%M%p}' } } as object),
              ...(tickvals.length > 0 && {
                tickvals: tickvals,
                ticktext: ticktext,
                tickmode: 'array'
              })
            },
            yaxis: createYAxisConfig('left', leftAutoRangeDisplay, getEffectiveFloor(l.val)),
            ...(secondaryConfig && r.val !== 'none' && { yaxis2: createYAxisConfig('right', rightAutoRangeDisplay, getEffectiveFloor(r.val as Metric)) }),
            margin: isOgMode
              ? { l: 50, r: 50, t: 10, b: 115 }
              : { l: 35, r: secondaryConfig ? 35 : 10, t: 5, b: 45 },
            hovermode: 'x unified',
            hoverlabel: {
              bgcolor: plotColors.plotBg,
              bordercolor: plotColors.gridcolor,
              font: {
                color: plotColors.textColor,
                size: fontSizes.tick
              }
            },
            plot_bgcolor: plotColors.plotBg,
            paper_bgcolor: plotColors.plotBg,
            dragmode: 'pan',
            showlegend: false,
            selectdirection: 'h'
          }}
          config={{
            displayModeBar: false,
            displaylogo: false,
            responsive: true,
            scrollZoom: true,
            doubleClick: false,
            showTips: false,
            modeBarButtonsToRemove: ['select2d', 'lasso2d', 'zoomIn2d', 'zoomOut2d', 'autoScale2d', 'resetScale2d'],
            toImageButtonOptions: {
              format: 'png',
              filename: 'awair-chart',
              height: 500,
              width: 1000,
              scale: 1
            }
          }}
          disableTheme
          disableLegendHover
          disableSoloTrace
          disableHoverDismiss
          disableMobileFix
          onRelayout={handleRelayout}
          onDoubleClick={handleDoubleClick}
          onInitialized={() => {
            (window as Window & { chartReady?: boolean }).chartReady = true
            setupHoverDismiss()
          }}
          onAfterPlot={() => {
            (window as Window & { chartReady?: boolean }).chartReady = true
            setupHoverDismiss()
          }}
        />
      </div>

      {!isOgMode && (
        <ChartControls
          {...{
            metrics,
            deviceRenderStrategy, setDeviceRenderStrategy,
            hsvConfig, setHsvConfig,
            xAxisRange, setXAxisRange,
            data,
            summary,
            latestModeIntended, setLatestModeIntended,
            setDuration,
            getActiveTimeRange,
            handleTimeRangeClick,
            handleAllClick,
            setRangeByWidth,
            setIgnoreNextPanCheck,
            devices,
            selectedDeviceIds,
            onDeviceSelectionChange,
            onPreviewDeviceIds: setPreviewDeviceIds,
            onHoverDeviceId,
            deviceLatestTimestamps,
            selectedWindow,
            validWindows,
            timeRangeMinutes,
            smoothing,
            onSmoothingChange: setSmoothing,
            stddevOpacity,
            onStddevOpacityChange: setStddevOpacity,
            rawOpacity,
            onRawOpacityChange: setRawOpacity,
          }}
          onWindowChange={window => {
            if (window) {
              setXGrouping({ mode: 'fixed', windowLabel: window.label })
            }
          }}
          targetPx={targetPx as PxOption | null}
          onTargetPxChange={px => {
            if (px === null) {
              // Switch to fixed mode, keep current window
              setXGrouping({ mode: 'fixed', windowLabel: selectedWindow.label })
            } else {
              setXGrouping({ mode: 'auto', targetPx: px })
            }
          }}
          containerWidth={viewportWidth}
        />
      )}

      {!isOgMode && <ExamplesBar />}

      {!isOgMode && (
        <DataTable
          data={aggregatedData}
          isRawData={isRawData}
          totalDataCount={tableMetadata.totalDataCount}
          rawDataCount={tableMetadata.rawDataCount}
          windowLabel={selectedWindow.label}
          fullDataStartTime={tableMetadata.fullDataStartTime}
          fullDataEndTime={tableMetadata.fullDataEndTime}
          windowMinutes={selectedWindow.minutes}
          deviceAggregations={displayDeviceAggregations}
          selectedDeviceId={selectedDeviceIdForTable}
          onDeviceChange={setSelectedDeviceIdForTable}
          timeRange={timeRangeFromProps}
          setTimeRange={setTimeRangeFromProps}
          pageSize={tablePageSize}
          onPageSizeChange={(size) => setTablePageSize(size as 10 | 20 | 50 | 100 | 200)}
        />
      )}

    </div>
  )
})
